import client from './client';
import {
  TimesheetExceptionsPayload, ExceptionReviewResult, ReviewRequestAction,
} from '../interfaces/TimesheetExceptions';

/**
 * Timesheet Exceptions API client. Mirrors src/routes/admin/timesheetExceptions.ts.
 * Error-message helpers follow the reportsConfiguration.ts pattern.
 */

function exceptionsErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string; message?: string } } }).response;
    if (response?.data?.error) return response.data.error;
    if (response?.data?.message) return response.data.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function getTimesheetExceptionsErrorMessage(err: unknown): string {
  return exceptionsErrorMessage(err, 'An error occurred while loading timesheet exceptions.');
}

export function exportExceptionsErrorMessage(err: unknown): string {
  return exceptionsErrorMessage(err, 'An error occurred while building the export.');
}

export function setTimesheetExceptionReviewErrorMessage(err: unknown): string {
  return exceptionsErrorMessage(err, 'An error occurred while updating the review state.');
}

/**
 * HTTP status of a failed review PATCH, or undefined for a network error. The page
 * treats 409 (state changed under you → refetch) and 503 (review DDL not applied on
 * this tenant → hide the controls) differently from a plain failure.
 */
export function reviewErrorStatus(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    return (err as { response?: { status?: number } }).response?.status;
  }
  return undefined;
}

/**
 * PATCH /admin/timesheetExceptions/:id/review. 'review' needs the submission to be
 * unreviewed; 'editNote' and 'clear' need it reviewed (otherwise 409). The note is
 * trimmed server-side, blank → null, and ignored on 'clear'.
 */
export async function setTimesheetExceptionReview(
  timesheetExceptionEventID: number,
  action: ReviewRequestAction,
  note?: string | null,
): Promise<ExceptionReviewResult> {
  const response = await client.patch<ExceptionReviewResult>(
    `admin/timesheetExceptions/${timesheetExceptionEventID}/review`,
    action === 'clear' ? { action } : { action, note: note ?? null },
  );
  return response.data;
}

export async function getTimesheetExceptions(payPeriodID: number): Promise<TimesheetExceptionsPayload> {
  const response = await client.get<TimesheetExceptionsPayload>('admin/timesheetExceptions', {
    params: { payPeriodID },
  });
  return response.data;
}

/** Server-generated workbook, downloaded as a blob (the app has no client-side xlsx). */
export async function downloadExceptionsExcel(payPeriodID: number, weekNumber?: number): Promise<void> {
  const response = await client.get('admin/timesheetExceptions/excel', {
    params: { payPeriodID },
    responseType: 'blob',
  });

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `TimesheetExceptions_Week${weekNumber ?? payPeriodID}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
